/**
 * Wristband Service API Contracts
 * Request/Response schemas for wristband issuance, lifecycle, credits, printing, scanning, and fraud.
 *
 * Wristband QR format: {WristbandID}-{Timestamp}-{HMAC}-{Counter}
 * Credit balance is ALWAYS stored server-side — never on the wristband chip.
 *
 * These schemas mirror the real wristband-service DTOs exactly; do not change field shapes
 * without a corresponding change to the service implementation.
 */

import { z } from 'zod';

// ============================================================================
// Enums
// ============================================================================

export const WristbandStatusSchema = z.enum([
  'PENDING',
  'ISSUED',
  'ACTIVE',
  'SUSPENDED',
  'VOIDED',
  'EXPIRED',
  'REPLACED',
]);

export type WristbandStatus = z.infer<typeof WristbandStatusSchema>;

export const IssuanceTypeSchema = z.enum([
  'KIOSK',
  'BOOTH',
  'WILL_CALL',
  'GROUP_CHECK_IN',
  'PREPAID_MAIL',
]);

export type IssuanceType = z.infer<typeof IssuanceTypeSchema>;

export const VoidReasonSchema = z.enum([
  'LOST',
  'DAMAGED',
  'EXPIRED',
  'CUSTOMER_REQUEST',
  'FRAUD_SUSPECTED',
  'REPLACEMENT_ISSUED',
  'SYSTEM_ERROR',
]);

export type VoidReason = z.infer<typeof VoidReasonSchema>;

export const CreditSourceSchema = z.enum([
  'purchase',
  'topup',
  'promo',
  'refund',
  'adjustment',
]);

export type CreditSource = z.infer<typeof CreditSourceSchema>;

export const DeductReasonSchema = z.enum([
  'attraction',
  'purchase',
  'expiry',
  'adjustment',
]);

export type DeductReason = z.infer<typeof DeductReasonSchema>;

export const ReplacementReasonSchema = z.enum(['LOST', 'DAMAGED', 'MALFUNCTION']);

export type ReplacementReason = z.infer<typeof ReplacementReasonSchema>;

export const PrinterTypeSchema = z.enum(['TICKET_PRINTER', 'WRISTBAND_PRINTER']);

export type PrinterType = z.infer<typeof PrinterTypeSchema>;

export const PrinterStatusSchema = z.enum([
  'ONLINE',
  'OFFLINE',
  'ERROR',
  'MAINTENANCE',
  'DECOMMISSIONED',
]);

export type PrinterStatus = z.infer<typeof PrinterStatusSchema>;

export const FraudAlertStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'REJECTED',
  'AUTO_RESOLVED',
]);

export type FraudAlertStatus = z.infer<typeof FraudAlertStatusSchema>;

export const FraudAlertSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);

export type FraudAlertSeverity = z.infer<typeof FraudAlertSeveritySchema>;

/** Status vocabulary used by the public-facing wristband lookup endpoint only */
export const PublicWristbandStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'VOID', 'LOST']);

export type PublicWristbandStatus = z.infer<typeof PublicWristbandStatusSchema>;

// ============================================================================
// Wristband Entity (returned by internal CRUD endpoints)
// ============================================================================

export const WristbandDetailSchema = z.object({
  id: z.string().uuid(),
  wristbandCode: z.string().max(50),
  qrContent: z.string().max(100),
  qrCodeBase64: z.string(),
  hmacTruncated: z.string(),
  status: WristbandStatusSchema,
  ticketId: z.string().uuid(),
  orderId: z.string(),
  customerId: z.string(),
  creditBalance: z.number().int().nonnegative(),
  issuedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime(),
  lastScanAt: z.string().datetime().nullable(),
  lastScanLocation: z.string().nullable(),
});

export type WristbandDetail = z.infer<typeof WristbandDetailSchema>;

// ============================================================================
// Wristband Issuance
// ============================================================================

export const IssueWristbandRequestSchema = z.object({
  ticketId: z.string().uuid(),
  orderId: z.string().max(50),
  customerId: z.string(),
  issuanceType: IssuanceTypeSchema,
  issuanceLocation: z.string().max(200),
  customerName: z.string().max(200).optional(),
  ticketType: z.string().max(100).optional(),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  initialCredits: z.number().int().min(0).optional(),
  expiresAt: z.string().datetime().optional(),
  printerId: z.string().max(100).optional(),
  staffId: z.string().max(100).optional(),
  wristbandId: z.string().max(50).optional(),
  qrContent: z.string().max(100).optional(),
  hmacTruncated: z.string().max(100).optional(),
  timestampIssued: z.number().int().optional(),
});

export type IssueWristbandRequest = z.infer<typeof IssueWristbandRequestSchema>;

export const IssuedWristbandResponseSchema = z.object({
  id: z.string().uuid(),
  wristbandCode: z.string(),
  qrContent: z.string(),
  qrCodeBase64: z.string(),
  status: WristbandStatusSchema,
  ticketId: z.string().uuid(),
  orderId: z.string(),
  customerId: z.string(),
  creditBalance: z.number().int().nonnegative(),
  hmacTruncated: z.string(),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});

export type IssuedWristbandResponse = z.infer<typeof IssuedWristbandResponseSchema>;

export const BatchTicketItemSchema = z.object({
  ticketId: z.string().uuid(),
  customerId: z.string(),
  customerName: z.string().max(200).optional(),
  ticketType: z.string().max(100).optional(),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  initialCredits: z.number().int().min(0).optional(),
  expiresAt: z.string().datetime().optional(),
  wristbandId: z.string().max(50).optional(),
  qrContent: z.string().max(100).optional(),
  hmacTruncated: z.string().max(100).optional(),
  timestampIssued: z.number().int().optional(),
});

export type BatchTicketItem = z.infer<typeof BatchTicketItemSchema>;

export const BatchIssueWristbandRequestSchema = z.object({
  orderId: z.string().max(50),
  tickets: z.array(BatchTicketItemSchema).min(1).max(100),
  issuanceType: IssuanceTypeSchema,
  issuanceLocation: z.string().max(200),
  printerId: z.string().max(100).optional(),
  staffId: z.string().max(100).optional(),
});

export type BatchIssueWristbandRequest = z.infer<typeof BatchIssueWristbandRequestSchema>;

export const BatchIssuanceResponseSchema = z.object({
  orderId: z.string(),
  totalRequested: z.number().int().nonnegative(),
  totalCreated: z.number().int().nonnegative(),
  wristbands: z.array(IssuedWristbandResponseSchema),
  errors: z.array(z.record(z.unknown())),
});

export type BatchIssuanceResponse = z.infer<typeof BatchIssuanceResponseSchema>;

// ============================================================================
// Wristband CRUD
// ============================================================================

export const CreateWristbandRequestSchema = z.object({
  ticketId: z.string().uuid(),
  orderId: z.string().max(50),
  customerId: z.string(),
  initialCredits: z.number().int().min(0).optional(),
  expiresAt: z.string().datetime().optional(),
  issuanceType: IssuanceTypeSchema.optional(),
  issuanceLocation: z.string().max(200).optional(),
  customerName: z.string().max(200).optional(),
  ticketType: z.string().max(100).optional(),
  printerId: z.string().max(100).optional(),
  staffId: z.string().max(100).optional(),
  wristbandId: z.string().max(50),
  qrContent: z.string().max(100),
  hmacTruncated: z.string().max(100).optional(),
  timestampIssued: z.number().int().optional(),
});

export type CreateWristbandRequest = z.infer<typeof CreateWristbandRequestSchema>;

export const IssuePendingWristbandRequestSchema = z.object({
  issuanceLocation: z.string().max(200),
  printerId: z.string().max(100).optional(),
  staffId: z.string().max(100).optional(),
});

export type IssuePendingWristbandRequest = z.infer<typeof IssuePendingWristbandRequestSchema>;

export const VoidWristbandRequestSchema = z.object({
  reason: VoidReasonSchema,
  notes: z.string().max(500).optional(),
  staffId: z.string().max(100).optional(),
});

export type VoidWristbandRequest = z.infer<typeof VoidWristbandRequestSchema>;

export const GetWristbandsQuerySchema = z.object({
  customerId: z.string().optional(),
  ticketId: z.string().uuid().optional(),
  status: WristbandStatusSchema.optional(),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetWristbandsQuery = z.infer<typeof GetWristbandsQuerySchema>;

/** Safe public-facing lookup response — excludes internal IDs, HMAC secrets, QR content, PII */
export const PublicWristbandTransactionSchema = z.object({
  id: z.string(),
  type: z.enum(['TOP_UP', 'DEBIT', 'REFUND']),
  amount: z.number().int(),
  timestamp: z.string().datetime(),
  attractionName: z.string().optional(),
  description: z.string().optional(),
});

export type PublicWristbandTransaction = z.infer<typeof PublicWristbandTransactionSchema>;

export const PublicWristbandScanSchema = z.object({
  id: z.string(),
  timestamp: z.string().datetime(),
  attractionName: z.string().optional(),
  gateLabel: z.string().optional(),
});

export type PublicWristbandScan = z.infer<typeof PublicWristbandScanSchema>;

export const PublicWristbandLookupSchema = z.object({
  wristbandId: z.string().max(50),
  status: PublicWristbandStatusSchema,
  creditBalanceIdr: z.number().int().nonnegative(),
  lastTopUpAt: z.string().datetime().nullable(),
  lastTopUpAmount: z.number().int().nullable(),
  totalUsedToday: z.number().int().nonnegative(),
  transactions: z.array(PublicWristbandTransactionSchema),
  scans: z.array(PublicWristbandScanSchema),
});

export type PublicWristbandLookup = z.infer<typeof PublicWristbandLookupSchema>;

// ============================================================================
// Wristband Lifecycle
// ============================================================================

export const SuspendWristbandRequestSchema = z.object({
  reason: z.string().max(500).optional(),
  staffId: z.string().max(100),
});

export type SuspendWristbandRequest = z.infer<typeof SuspendWristbandRequestSchema>;

export const ReactivateWristbandRequestSchema = z.object({
  staffId: z.string().max(100),
});

export type ReactivateWristbandRequest = z.infer<typeof ReactivateWristbandRequestSchema>;

export const DeactivateWristbandRequestSchema = z.object({
  reason: z.string().max(500).optional(),
  staffId: z.string().max(100),
});

export type DeactivateWristbandRequest = z.infer<typeof DeactivateWristbandRequestSchema>;

export const ReportLostWristbandRequestSchema = z.object({
  reason: z.enum(['LOST', 'DAMAGED']).default('LOST'),
  notes: z.string().max(500).optional(),
  staffId: z.string().max(100).optional(),
});

export type ReportLostWristbandRequest = z.infer<typeof ReportLostWristbandRequestSchema>;

export const ReplaceWristbandRequestSchema = z.object({
  staffId: z.string().max(100),
  newTicketId: z.string().uuid().optional(),
  reason: ReplacementReasonSchema,
});

export type ReplaceWristbandRequest = z.infer<typeof ReplaceWristbandRequestSchema>;

export const SuccessMessageResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type SuccessMessageResponse = z.infer<typeof SuccessMessageResponseSchema>;

// ============================================================================
// Credits
// ============================================================================

export const AddCreditsRequestSchema = z.object({
  amount: z.number().int().min(1),
  source: CreditSourceSchema,
  referenceType: z.string().max(100).optional(),
  referenceId: z.string().max(100).optional(),
  idempotencyKey: z.string().max(100),
  description: z.string().max(500).optional(),
});

export type AddCreditsRequest = z.infer<typeof AddCreditsRequestSchema>;

export const DeductCreditsRequestSchema = z.object({
  amount: z.number().int().min(1),
  reason: DeductReasonSchema,
  attractionId: z.string().max(100).optional(),
  referenceId: z.string().max(100).optional(),
  idempotencyKey: z.string().max(100),
  description: z.string().max(500).optional(),
});

export type DeductCreditsRequest = z.infer<typeof DeductCreditsRequestSchema>;

export const TransferCreditsRequestSchema = z.object({
  targetWristbandId: z.string().max(100),
  amount: z.number().int().min(1),
  description: z.string().max(500).optional(),
});

export type TransferCreditsRequest = z.infer<typeof TransferCreditsRequestSchema>;

export const RefundCreditsRequestSchema = z.object({
  description: z.string().max(500).optional(),
});

export type RefundCreditsRequest = z.infer<typeof RefundCreditsRequestSchema>;

export const BalanceResponseSchema = z.object({
  wristbandId: z.string(),
  creditBalance: z.number().int().nonnegative(),
  todayAdded: z.number().int().nonnegative(),
  todayUsed: z.number().int().nonnegative(),
});

export type BalanceResponse = z.infer<typeof BalanceResponseSchema>;

// ============================================================================
// Printing
// ============================================================================

export const RegisterPrinterRequestSchema = z.object({
  printerId: z.string().max(100),
  name: z.string().max(200),
  location: z.string().max(200),
  printerType: PrinterTypeSchema,
  ipAddress: z.string().max(100).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  kioskId: z.string().max(100).optional(),
});

export type RegisterPrinterRequest = z.infer<typeof RegisterPrinterRequestSchema>;

export const UpdatePrinterRequestSchema = z.object({
  name: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  ipAddress: z.string().max(100).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  status: PrinterStatusSchema.optional(),
  kioskId: z.string().max(100).nullable().optional(),
});

export type UpdatePrinterRequest = z.infer<typeof UpdatePrinterRequestSchema>;

export const PrintWristbandRequestSchema = z.object({
  kioskId: z.string().max(100).optional(),
});

export type PrintWristbandRequest = z.infer<typeof PrintWristbandRequestSchema>;

export const HeartbeatRequestSchema = z.object({
  printerId: z.string().max(100),
  status: PrinterStatusSchema,
  paperLevel: z.number().int().min(0).max(100).optional(),
  errorMessage: z.string().max(500).optional(),
});

export type HeartbeatRequest = z.infer<typeof HeartbeatRequestSchema>;

export const PrintJobResponseSchema = z.object({
  jobId: z.string(),
  status: z.string(),
  printData: z.record(z.unknown()).optional(),
});

export type PrintJobResponse = z.infer<typeof PrintJobResponseSchema>;

export const PrinterSchema = z.object({
  id: z.string(),
  printerId: z.string(),
  name: z.string(),
  location: z.string(),
  printerType: PrinterTypeSchema,
  status: PrinterStatusSchema,
  ipAddress: z.string().nullable(),
  port: z.number().int().nullable(),
  kioskId: z.string().nullable(),
  lastHeartbeatAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Printer = z.infer<typeof PrinterSchema>;

export const GetPrintersQuerySchema = z.object({
  location: z.string().max(200).optional(),
  status: PrinterStatusSchema.optional(),
  kioskId: z.string().max(100).optional(),
});

export type GetPrintersQuery = z.infer<typeof GetPrintersQuerySchema>;

// ============================================================================
// Scanning
// ============================================================================

export const ScanActionSchema = z.enum([
  'PARK_ENTRY',
  'PARK_EXIT',
  'PARK_REENTRY',
  'ATTRACTION_ENTRY',
  'PURCHASE',
  'CREDIT_TOPUP',
  'PHOTO_LINK',
  'INFO_LOOKUP',
]);

export type ScanAction = z.infer<typeof ScanActionSchema>;

export const ScannerTypeSchema = z.enum([
  'GATE',
  'ATTRACTION',
  'KIOSK',
  'POS',
  'MOBILE',
]);

export type ScannerType = z.infer<typeof ScannerTypeSchema>;

export const ScanRequestSchema = z.object({
  qrContent: z.string(),
  scannerId: z.string(),
  scannerType: ScannerTypeSchema,
  locationId: z.string(),
  locationName: z.string(),
  scanAction: ScanActionSchema,
  attractionId: z.string().optional(),
  creditsRequired: z.number().int().nonnegative().optional(),
});

export type ScanRequest = z.infer<typeof ScanRequestSchema>;

export const QuickValidateRequestSchema = z.object({
  qrContent: z.string(),
  scannerId: z.string(),
});

export type QuickValidateRequest = z.infer<typeof QuickValidateRequestSchema>;

export const ScanHistoryQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  scanAction: ScanActionSchema.optional(),
});

export type ScanHistoryQuery = z.infer<typeof ScanHistoryQuerySchema>;

export const ScanRecordSchema = z.object({
  id: z.string(),
  scanAction: ScanActionSchema,
  locationName: z.string(),
  scanResult: z.string(),
  counterValue: z.number().int(),
  creditsUsed: z.number().int().optional(),
  attractionId: z.string().optional(),
  denialReason: z.string().optional(),
  scanDurationMs: z.number().int(),
  createdAt: z.string().datetime(),
});

export type ScanRecord = z.infer<typeof ScanRecordSchema>;

export const ScanHistoryResponseSchema = z.object({
  wristbandId: z.string(),
  scans: z.array(ScanRecordSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});

export type ScanHistoryResponse = z.infer<typeof ScanHistoryResponseSchema>;

export const JourneyStopSchema = z.object({
  time: z.string().datetime(),
  action: ScanActionSchema,
  location: z.string(),
  result: z.string(),
  creditsUsed: z.number().int().optional(),
  attractionId: z.string().optional(),
});

export type JourneyStop = z.infer<typeof JourneyStopSchema>;

export const JourneyTimelineSchema = z.object({
  wristbandId: z.string(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  stops: z.array(JourneyStopSchema),
});

export type JourneyTimeline = z.infer<typeof JourneyTimelineSchema>;

export const ScanStatsSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalScans: z.number().int().nonnegative(),
  uniqueWristbands: z.number().int().nonnegative(),
  scansByAction: z.record(z.number().int()),
  scansByResult: z.record(z.number().int()),
  avgScanDurationMs: z.number().nonnegative(),
});

export type ScanStats = z.infer<typeof ScanStatsSchema>;

// ============================================================================
// Fraud
// ============================================================================

export const FraudQuerySchema = z.object({
  status: FraudAlertStatusSchema.optional(),
  severity: FraudAlertSeveritySchema.optional(),
  alertType: z.string().optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type FraudQuery = z.infer<typeof FraudQuerySchema>;

export const ReviewFraudRequestSchema = z.object({
  reviewNote: z.string().max(2000).optional(),
});

export type ReviewFraudRequest = z.infer<typeof ReviewFraudRequestSchema>;

export const FraudAlertSchema = z.object({
  id: z.string().uuid(),
  wristbandId: z.string(),
  alertType: z.string(),
  severity: FraudAlertSeveritySchema,
  status: FraudAlertStatusSchema,
  description: z.string(),
  reviewNote: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export type FraudAlert = z.infer<typeof FraudAlertSchema>;

export const FraudSummarySchema = z.object({
  totalPending: z.number().int().nonnegative(),
  totalApproved: z.number().int().nonnegative(),
  totalRejected: z.number().int().nonnegative(),
  totalAutoResolved: z.number().int().nonnegative(),
  bySeverity: z.record(z.number().int().nonnegative()),
});

export type FraudSummary = z.infer<typeof FraudSummarySchema>;

export const FraudListResponseSchema = z.object({
  alerts: z.array(FraudAlertSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});

export type FraudListResponse = z.infer<typeof FraudListResponseSchema>;

// ============================================================================
// HMAC Key Rotation (admin)
// ============================================================================

export const HmacKeyStatusSchema = z.object({
  currentKeyId: z.string().max(50),
  previousKeyId: z.string().max(50).nullable(),
  rotatedAt: z.string().datetime().nullable(),
  nextRotationAt: z.string().datetime(),
  activeWristbandCount: z.number().int().nonnegative(),
});

export type HmacKeyStatus = z.infer<typeof HmacKeyStatusSchema>;

// ============================================================================
// Wristband Statistics
// ============================================================================

export const WristbandStatsSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalIssued: z.number().int().nonnegative(),
  totalActive: z.number().int().nonnegative(),
  totalVoided: z.number().int().nonnegative(),
  totalExpired: z.number().int().nonnegative(),
  totalCreditsLoaded: z.number().int().nonnegative(),
  totalCreditsUsed: z.number().int().nonnegative(),
  avgCreditsPerWristband: z.number().nonnegative(),
  totalScans: z.number().int().nonnegative(),
  avgScansPerWristband: z.number().nonnegative(),
});

export type WristbandStats = z.infer<typeof WristbandStatsSchema>;

// ============================================================================
// QR validation — wristband-service `POST /api/v1/wristbands/validate-qr`
// ============================================================================
//
// This endpoint bound `@Body('qrContent') qrContent: string`, extracting a single
// property. A pipe placed on that parameter never sees the request object, so a
// body without `qrContent` arrived as `undefined` and the validator failed on
// `.split()` — a 500 for a missing field.
//
// Binding the whole object instead is what makes it checkable. The 38-character
// shape is `{WristbandID}-{Timestamp}-{HMAC}-{Counter}`; length is deliberately
// NOT constrained here because rejecting a malformed code is the service's job
// and its answer ("invalid") is more useful than a 400 that leaks the format.

export const ValidateWristbandQrSchema = z.object({
  qrContent: z.string().min(1),
});
export type ValidateWristbandQrRequest = z.infer<typeof ValidateWristbandQrSchema>;

export * from './admin';

export * from './kiosk-inter-service';
